import {
    Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { US_STATES } from '@shared/common/us-states';

/**
 * Structured address form for Address-type project fields (shared by the add-project wizard,
 * the view-project Details tab and the task slide-over). US mode: street/suite/city/State
 * (static state list)/ZIP; international mode: street/suite/city/state-province/postal/country.
 * Emits the structured parts plus the comma-separated `formatted` display string; the caller
 * persists `{formatted, ...parts}` (the backend keeps the full JSON in ProjectLocationData and
 * the formatted string in the ProjectLocation column).
 */
@Component({
    selector: 'vf-address-editor',
    templateUrl: './address-editor.component.html',
    styles: [`
        .vf-addr-card { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 12px; }
        .vf-addr-label { font-size: 13px; color: #4b5563; margin-bottom: 4px; display: block; }
        .vf-addr-label .vf-addr-opt { color: #9ca3af; }
        .vf-addr-input {
            width: 100%; border: 0; border-radius: 4px; background: #f3f4f6;
            min-height: 36px; padding: 6px 10px; font-size: 14px; color: #111827; outline: none;
        }
        .vf-addr-input::placeholder { color: #9ca3af; }
        .vf-addr-toggle {
            width: 36px; height: 20px; border-radius: 999px; background: #d1d5db;
            border: none; padding: 0; position: relative; cursor: pointer;
            transition: background-color .15s ease; flex-shrink: 0;
        }
        .vf-addr-toggle.is-on { background: #111827; }
        .vf-addr-toggle .vf-addr-knob {
            position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%;
            background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.15); transition: transform .15s ease;
        }
        .vf-addr-toggle.is-on .vf-addr-knob { transform: translateX(16px); }
        /* Scrolling lives on the inner list so the search box stays pinned at the top. */
        .vf-addr-state-menu { width: 100%; min-width: 100%; }
        .vf-addr-state-menu input.form-control:focus {
            box-shadow: none !important;
            outline: none !important;
            border-top: none !important;
            border-left: none !important;
            border-right: none !important;
            border-bottom-color: #DEDEDE !important;
            background: #fff !important;
        }
    `],
})
export class AddressEditorComponent implements OnChanges {
    /** Stored value: structured parts, legacy {formatted}, a bare string, or null. */
    @Input() value: any;
    /** Location-list mode: adds a Name field above and a URL field below the address. */
    @Input() showNameUrl = false;
    @Output() saved = new EventEmitter<any>();
    @Output() cancelled = new EventEmitter<void>();

    isUS = true;
    name = '';
    url = '';
    street = '';
    suite = '';
    city = '';
    state = '';
    zip = '';
    country = '';
    stateSearch = '';

    @ViewChild('streetInput') private streetInput: ElementRef<HTMLInputElement>;

    readonly usStates: string[] = US_STATES.map((state) => state.name);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value) {
            this.populateFromValue(this.value);
        }
    }

    private populateFromValue(v: any): void {
        let hasParts = false;

        this.isUS = true;
        this.name = '';
        this.url = '';
        this.street = '';
        this.suite = '';
        this.city = '';
        this.state = '';
        this.zip = '';
        this.country = '';

        if (v === undefined || v === null) {
            return;
        }
        if (typeof v === 'string') {
            this.street = v;
            return;
        }
        this.name = v.name || '';
        this.url = v.url || '';
        if (v.isUS !== undefined) {
            this.isUS = v.isUS !== false;
        }
        hasParts = !!(v.street || v.suite || v.city || v.state || v.zip || v.country);
        if (hasParts) {
            this.street = v.street || '';
            this.suite = v.suite || '';
            this.city = v.city || '';
            this.state = v.state || '';
            this.zip = v.zip || '';
            this.country = v.country || '';
        } else if (v.formatted) {
            // Legacy free-text value — keep it editable rather than silently dropping it.
            this.street = v.formatted;
        }
    }

    /**
     * Put the caret in the first field. For callers that open this form in a popup — the grid's address cell
     * editor — where nothing else would take focus, so Esc and Tab would otherwise be handled by whatever is
     * behind the form rather than by the form itself.
     */
    focusFirstField(): void {
        if (this.streetInput && this.streetInput.nativeElement) {
            this.streetInput.nativeElement.focus();
        }
    }

    toggleUS(): void {
        this.isUS = !this.isUS;
        // The state list differs between modes; a US state name rarely makes sense abroad and
        // vice versa, so clear it on switch (street/suite/city/zip carry over fine).
        this.state = '';
        if (this.isUS) {
            this.country = '';
        }
    }

    selectState(s: string): void {
        this.state = s;
    }

    /** States matching the menu's search text (all of them when the search is empty). */
    filteredStates(): string[] {
        let q = (this.stateSearch || '').trim().toLowerCase();
        if (q === '') {
            return this.usStates;
        }
        return this.usStates.filter(s => s.toLowerCase().indexOf(q) >= 0);
    }

    /** Popper positions the menu once on open; when filtering shrinks the list (especially with
     *  the menu flipped above the field) it would float with a gap — reposition for the new height. */
    onStateSearchChange(): void {
        setTimeout(() => {
            // 'update' re-runs Popper positioning (a documented BS4 method) — the installed
            // typings only declare 'toggle', so go through any.
            ($('.vf-addr-state-drop [data-toggle="dropdown"]') as any).dropdown('update');
        }, 0);
    }

    /** Comma-separated display string, e.g. "123 Main St, Suite 200, Miami, Florida, 33101". */
    private buildFormatted(): string {
        let parts: string[] = [];

        [this.street, this.suite, this.city, this.state, this.zip].forEach(p => {
            if (p && p.trim() !== '') {
                parts.push(p.trim());
            }
        });
        if (!this.isUS && this.country && this.country.trim() !== '') {
            parts.push(this.country.trim());
        }
        return parts.join(', ');
    }

    save(): void {
        let out: any = {
            formatted: this.buildFormatted(),
            street: (this.street || '').trim(),
            suite: (this.suite || '').trim(),
            city: (this.city || '').trim(),
            state: (this.state || '').trim(),
            zip: (this.zip || '').trim(),
            country: this.isUS ? '' : (this.country || '').trim(),
            isUS: this.isUS,
        };
        if (this.showNameUrl) {
            out.name = (this.name || '').trim();
            out.url = (this.url || '').trim();
        }
        this.saved.emit(out);
    }

    cancel(): void {
        this.cancelled.emit();
    }
}
